import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Loader2, Printer, ArrowLeft } from 'lucide-react';

const TripPrint = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json'
  }), []);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/trips/${id}/detail`, { headers: getAuthHeaders() });
        if (!res.ok) throw new Error();
        setDetail(await res.json());
      } catch {
        alert('Failed to load trip detail');
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [backendUrl, id, getAuthHeaders]);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: '80px' }}><Loader2 className="w-8 h-8 animate-spin" /></div>;
  if (!detail) return <div style={{ textAlign: 'center', padding: '80px' }}>Trip not found</div>;

  const { trip, company, expenses, payments, logs, totals } = detail;

  // Categorize expenses
  const loadingExp = expenses.filter(e => e.expense_type === 'LOADING');
  const unloadingExp = expenses.filter(e => e.expense_type === 'UNLOADING');
  const dieselExp = expenses.filter(e => e.expense_type === 'DIESEL');
  const batthuExp = expenses.filter(e => e.expense_type === 'BATTHU' || e.expense_type === 'BHATTU');
  const ureaExp = expenses.filter(e => e.expense_type === 'UREA');
  const receiptExp = expenses.filter(e => !['LOADING','UNLOADING','DIESEL','BATTHU','BHATTU','UREA'].includes(e.expense_type));

  // Calculate trip amount from logs: per-leg rate × distance
  const rateAmount = trip.rate_amount || 0;
  const localFixedCharge = totals?.local_fixed_charge || 0;
  const isLocalFixed = totals?.is_local_fixed || false;
  // For local trips < 130 km: use fixed charge from vehicle, not km × rate
  const totalA = localFixedCharge > 0
    ? localFixedCharge
    : logs.reduce((sum, l) => sum + ((l.rate_amount ?? rateAmount) * (l.distance || 0)), 0);
  const totalB = loadingExp.reduce((s, e) => s + (e.amount || 0), 0);
  const totalC = unloadingExp.reduce((s, e) => s + (e.amount || 0), 0);
  const totalD = ureaExp.reduce((s, e) => s + (e.amount || 0), 0);
  const totalE = receiptExp.reduce((s, e) => s + (e.amount || 0), 0);
  const totalI = dieselExp.reduce((s, e) => s + (e.amount || 0), 0);

  // Driver allowance (F) - sum of batthu expenses for driver
  const driverBatthu = batthuExp.filter(e => (e.note || '').toLowerCase().includes('driver'));
  const totalF = driverBatthu.reduce((s, e) => s + (e.amount || 0), 0);

  // Conductor allowance (G) - sum of batthu expenses for conductor
  const conductorBatthu = batthuExp.filter(e => (e.note || '').toLowerCase().includes('conductor'));
  const totalG = conductorBatthu.reduce((s, e) => s + (e.amount || 0), 0);

  // NEFT/CASH = payments (includes auto-advance entry)
  const totalJ = payments.reduce((s, p) => s + (p.amount || 0), 0);

  const totalH = totalA + totalB + totalC + totalD + totalE + totalF + totalG;
  const totalK = totalI + totalJ;
  const balance = totalK - totalH;

  // Opening Balance (Previous Dues) — auto-carried from last completed trip of same driver
  const openingBalance = totals?.opening_balance || 0;
  const prevTripInfo = totals?.previous_trip_info || null;
  const finalBalance = balance - openingBalance;
  // Trip dates
  const logDates = logs.filter(l => l.start_date || l.end_date).flatMap(l => [l.start_date, l.end_date].filter(Boolean));
  const tripStartDt = logDates.length > 0 ? logDates.sort()[0] : trip.trip_date;
  const tripEndDt = logDates.length > 0 ? logDates.sort().reverse()[0] : trip.trip_date;

  // Days calculation
  const daysDiff = tripStartDt && tripEndDt ? Math.max(1, Math.ceil((new Date(tripEndDt) - new Date(tripStartDt)) / 86400000) + 1) : 1;

  // Driver batthu rate
  const driverRate = totalF > 0 ? Math.round(totalF / daysDiff) : 0;
  const conductorRate = totalG > 0 ? Math.round(totalG / daysDiff) : 0;

  const formatDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2, '0')}-${String(dt.getMonth() + 1).padStart(2, '0')}-${dt.getFullYear()}`;
  };

  const formatNum = (n) => n != null && n !== 0 ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: n % 1 !== 0 ? 2 : 0 }) : '0';

  // Rate type label
  const rateTypeLabel = (rt) => {
    const map = {
      minus_temp_rate: 'Minus Temp Rate',
      plus_temp_rate: 'Plus Temp Rate',
      general_rate: 'General Rate',
      cross_border_rate: 'Cross Border Rate',
      local_temp: 'Local Temp',
      local_gen: 'Local General',
    };
    return map[rt] || rt || '';
  };

  const tripTypeLabel = (tt) => (tt || 'local') === 'local' ? 'Local' : 'Out Station';

  // Max rows for expense sections display
  const maxOtherRows = Math.max(dieselExp.length, ureaExp.length, receiptExp.length, loadingExp.length, unloadingExp.length, 2);

  return (
    <>
      {/* Print controls */}
      <div className="print:hidden fixed top-4 left-4 right-4 z-50 flex justify-between items-center">
        <Button variant="outline" onClick={() => navigate(-1)} className="bg-white shadow-md">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button onClick={() => window.print()} className="bg-accent-blue hover:bg-accent-blue/90 text-white shadow-md">
          <Printer className="w-4 h-4 mr-2" /> Print / Save PDF
        </Button>
      </div>

      <div id="trip-print-area" data-testid="trip-print-sheet">
        <style>{`
          @media print {
            html, body { margin: 0; padding: 0; }
            body * { visibility: hidden; }
            #trip-print-area, #trip-print-area * { visibility: visible; }
            #trip-print-area { position: absolute; left: 0; top: 0; width: 100%; }
            .print\\:hidden { display: none !important; }
            @page { size: A4 portrait; margin: 8mm; }
          }
          #trip-print-area {
            font-family: Arial, sans-serif;
            font-size: 10.5px;
            color: #1a1a1a;
            width: 194mm;          /* A4 portrait minus 2 × 8mm margins */
            margin: 80px auto 20px;
            background: #fff;
            box-sizing: border-box;
          }
          @media print {
            #trip-print-area { margin: 0; width: 100%; font-size: 9.5px; }
          }
          .tp-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
          .tp-table td, .tp-table th { border: 1px solid #333; padding: 2px 5px; vertical-align: top; word-wrap: break-word; }
          .tp-table th { background: #f5f5f5; font-weight: 700; text-transform: uppercase; font-size: 9px; letter-spacing: 0.2px; }
          .tp-header-row td { border: none; padding: 0; }
          .tp-bold { font-weight: 700; }
          .tp-right { text-align: right; }
          .tp-center { text-align: center; }
          .tp-label { font-weight: 700; text-transform: uppercase; font-size: 9.5px; background: #f9f9f9; }
          .tp-total-label { font-weight: 700; text-transform: uppercase; font-size: 9.5px; }
          .tp-section-header { background: #e8e8e8; font-weight: 700; text-transform: uppercase; text-align: center; font-size: 9.5px; }
          .tp-empty { height: 16px; }
        `}</style>

        <table className="tp-table">
          <colgroup>
            <col style={{ width: '9%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '5.5%' }} />
            <col style={{ width: '9.5%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <tbody>
            {/* ===== HEADER: Logo + Company Info (dynamic, per trip's company) ===== */}
            <tr className="tp-header-row">
              <td colSpan={11} style={{ border: '1px solid #333', padding: 0 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr>
                      <td style={{ width: '45%', padding: '8px 12px', verticalAlign: 'middle', borderRight: '1px solid #333', textAlign: 'center' }}>
                        {company?.logo_url ? (
                          <img
                            src={company.logo_url}
                            alt={company.name || 'Company Logo'}
                            style={{ maxHeight: '70px', maxWidth: '100%', objectFit: 'contain' }}
                            onError={(e) => {
                              const fileId = company.logo_url?.match(/id=([^&]+)/)?.[1];
                              if (fileId && !e.target.dataset.retried) {
                                e.target.dataset.retried = 'true';
                                e.target.src = `https://drive.google.com/thumbnail?id=${fileId}&sz=w200`;
                              } else {
                                e.target.style.display = 'none';
                              }
                            }}
                          />
                        ) : (
                          <div style={{ fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '22px', fontWeight: 900, color: '#1a5ab8', letterSpacing: '-0.5px' }}>
                            {company?.name || 'COMPANY'}
                          </div>
                        )}
                      </td>
                      <td style={{ width: '55%', padding: '6px 12px', verticalAlign: 'top', textAlign: 'right', fontSize: '9.5px', lineHeight: '1.45' }}>
                        <div style={{ fontWeight: 700, fontSize: '12px', color: '#1a5ab8' }}>
                          {company?.name || ''}
                        </div>
                        {company?.address && <div>{company.address}</div>}
                        {(company?.city || company?.state || company?.pincode) && (
                          <div>{[company?.city, company?.state, company?.pincode].filter(Boolean).join(', ')}{company?.country ? `, ${company.country}` : ''}</div>
                        )}
                        {(company?.email || company?.phone) && (
                          <div>
                            {company?.email ? <>(E) {company.email}</> : null}
                            {company?.phone ? <> &nbsp;(M) {company.phone}</> : null}
                            {company?.phone_secondary ? <> &nbsp;(O) {company.phone_secondary}</> : null}
                          </div>
                        )}
                        {(company?.gst_number || company?.cin_number) && (
                          <div>
                            {company?.gst_number && <><strong>GST:</strong> {company.gst_number} </>}
                            {company?.cin_number && <><strong>CIN:</strong> {company.cin_number}</>}
                          </div>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>

            {/* ===== VEHICLE / DRIVER INFO ===== */}
            <tr>
              <td colSpan={2} className="tp-label">VEHICLE #</td>
              <td colSpan={2}>{trip.vehicle_registration || ''}</td>
              <td className="tp-label">SIZE</td>
              <td>{trip.vehicle_size || ''}</td>
              <td className="tp-label">DRIVER/S</td>
              <td colSpan={4}>{trip.driver_name || ''}</td>
            </tr>
            <tr>
              <td colSpan={2} className="tp-label">LR #</td>
              <td colSpan={2}>{trip.dockets?.map(d => d.system_docket_no).join(', ') || ''}</td>
              <td colSpan={3} className="tp-label"></td>
              <td className="tp-label">VOUCHER #</td>
              <td>{trip.trip_number}</td>
              <td className="tp-label">DATE</td>
              <td>{formatDate(trip.trip_date)}</td>
            </tr>

            {/* ===== ROUTE ===== */}
            <tr>
              <td className="tp-label">ROUTE</td>
              <td colSpan={10}>{trip.route || trip.from_location && trip.to_location ? `${trip.from_location} → ${trip.to_location}` : '—'}</td>
            </tr>

            {/* ===== TRIP LOGS TABLE HEADER ===== */}
            <tr>
              <th>ST. DATE</th>
              <th>LOCATION</th>
              <th>START KM</th>
              <th>LOCATION</th>
              <th>ED. DATE</th>
              <th>STOP KM</th>
              <th>DIFF</th>
              <th>TEMP/GEN</th>
              <th>LOCAL/OUT</th>
              <th>RATE</th>
              <th>AMOUNT</th>
            </tr>

            {/* Trip Log Rows */}
            {logs.length > 0 ? logs.map((l, i) => (
              <tr key={i}>
                <td>{formatDate(l.start_date)}</td>
                <td>{l.start_location || ''}</td>
                <td className="tp-right">{formatNum(l.start_km)}</td>
                <td>{l.end_location || ''}</td>
                <td>{formatDate(l.end_date)}</td>
                <td className="tp-right">{formatNum(l.end_km)}</td>
                <td className="tp-right">{l.distance > 0 ? formatNum(l.distance) : ''}</td>
                <td>{rateTypeLabel(l.rate_type || trip.rate_type)}</td>
                <td>{tripTypeLabel(trip.trip_type)}</td>
                <td className="tp-right">
                  {isLocalFixed ? (i === 0 ? 'Fixed' : '') : formatNum(l.rate_amount ?? rateAmount)}
                </td>
                <td className="tp-right tp-bold">
                  {isLocalFixed
                    ? (i === 0 ? formatNum(localFixedCharge) : '')
                    : (l.distance > 0 ? formatNum((l.rate_amount ?? rateAmount) * l.distance) : '')}
                </td>
              </tr>
            )) : (
              <tr><td colSpan={11} className="tp-empty"></td></tr>
            )}

            {/* Total (A) */}
            <tr>
              <td colSpan={9}></td>
              <td className="tp-total-label tp-right">Total (A)</td>
              <td className="tp-bold tp-right">{formatNum(totalA)}</td>
            </tr>

            {/* ===== OTHER EXP SECTION HEADER ===== */}
            <tr>
              <td colSpan={11} className="tp-section-header">OTHER EXP</td>
            </tr>

            {/* Sub-section headers */}
            <tr>
              <td colSpan={2} className="tp-center tp-bold" style={{ background: '#f0f0f0' }}>LOADING</td>
              <td colSpan={2} className="tp-center tp-bold" style={{ background: '#f0f0f0' }}>UNLOADING</td>
              <td colSpan={2} className="tp-center tp-bold" style={{ background: '#f0f0f0' }}>UREA</td>
              <td colSpan={3} className="tp-center tp-bold" style={{ background: '#f0f0f0' }}>RECEIPT EXP</td>
              <td colSpan={2} className="tp-center tp-bold" style={{ background: '#f0f0f0' }}>DIESEL</td>
            </tr>

            {/* Sub-section column headers */}
            <tr>
              <td className="tp-label">LR #</td>
              <td className="tp-label">AMOUNT</td>
              <td className="tp-label">LR #</td>
              <td className="tp-label">AMOUNT</td>
              <td className="tp-label">DATE</td>
              <td className="tp-label">AMOUNT</td>
              <td className="tp-label" colSpan={2}>EXPENSE</td>
              <td className="tp-label">AMOUNT</td>
              <td className="tp-label">DATE</td>
              <td className="tp-label">AMOUNT</td>
            </tr>

            {/* Expense rows */}
            {Array.from({ length: maxOtherRows }).map((_, i) => (
              <tr key={`exp-${i}`}>
                <td>{loadingExp[i]?.note || ''}</td>
                <td className="tp-right">{loadingExp[i] ? formatNum(loadingExp[i].amount) : ''}</td>
                <td>{unloadingExp[i]?.note || ''}</td>
                <td className="tp-right">{unloadingExp[i] ? formatNum(unloadingExp[i].amount) : ''}</td>
                <td>{ureaExp[i] ? formatDate(ureaExp[i].date) : ''}</td>
                <td className="tp-right">{ureaExp[i] ? formatNum(ureaExp[i].amount) : ''}</td>
                <td colSpan={2}>{receiptExp[i]?.expense_type || ''}</td>
                <td className="tp-right">{receiptExp[i] ? formatNum(receiptExp[i].amount) : ''}</td>
                <td>{dieselExp[i] ? formatDate(dieselExp[i].date) : ''}</td>
                <td className="tp-right">{dieselExp[i] ? formatNum(dieselExp[i].amount) : ''}</td>
              </tr>
            ))}

            {/* Totals row */}
            <tr>
              <td className="tp-total-label">TOTAL (B)</td>
              <td className="tp-bold tp-right">{formatNum(totalB)}</td>
              <td className="tp-total-label">TOTAL (C)</td>
              <td className="tp-bold tp-right">{formatNum(totalC)}</td>
              <td className="tp-total-label">TOTAL (D)</td>
              <td className="tp-bold tp-right">{formatNum(totalD)}</td>
              <td className="tp-total-label" colSpan={2}>TOTAL (E)</td>
              <td className="tp-bold tp-right">{formatNum(totalE)}</td>
              <td className="tp-total-label">TOTAL (I)</td>
              <td className="tp-bold tp-right">{formatNum(totalI)}</td>
            </tr>

            {/* ===== DRIVER ALLOWANCE ===== */}
            <tr>
              <td colSpan={6} className="tp-section-header">DRIVER ALLOWANCE</td>
              <td colSpan={5}></td>
            </tr>
            <tr>
              <th>TRIP START DT</th>
              <th>TRIP END DT</th>
              <th>DAYS</th>
              <th>RATE</th>
              <th colSpan={2}>AMOUNT (F)</th>
              <th colSpan={3}></th>
              <th>DATE</th>
              <th>NEFT/CASH</th>
            </tr>
            <tr>
              <td>{formatDate(tripStartDt)}</td>
              <td>{formatDate(tripEndDt)}</td>
              <td className="tp-center">{daysDiff}</td>
              <td className="tp-right">{formatNum(driverRate)}</td>
              <td colSpan={2} className="tp-right tp-bold">{formatNum(totalF)}</td>
              <td colSpan={3}></td>
              <td>{payments[0] ? formatDate(payments[0].date) : ''}</td>
              <td className="tp-right">{payments[0] ? formatNum(payments[0].amount) : ''}</td>
            </tr>

            {/* Additional payment rows */}
            {payments.slice(1).map((p, i) => (
              <tr key={`pay-${i}`}>
                <td colSpan={9}></td>
                <td>{formatDate(p.date)}</td>
                <td className="tp-right">{formatNum(p.amount)}</td>
              </tr>
            ))}

            {/* ===== CONDUCTOR / EXTRA DRIVER ALLOWANCE ===== */}
            <tr>
              <td colSpan={6} className="tp-section-header">CONDUCTOR / EXTRA DRIVER ALLOWANCE</td>
              <td colSpan={5}></td>
            </tr>
            <tr>
              <th>TRIP START DT</th>
              <th>TRIP END DT</th>
              <th>DAYS</th>
              <th>RATE</th>
              <th colSpan={2}>AMOUNT (G)</th>
              <th colSpan={5}></th>
            </tr>
            {trip.conductor_name ? (
              <tr>
                <td>{formatDate(tripStartDt)}</td>
                <td>{formatDate(tripEndDt)}</td>
                <td className="tp-center">{daysDiff}</td>
                <td className="tp-right">{formatNum(conductorRate)}</td>
                <td colSpan={2} className="tp-right tp-bold">{formatNum(totalG)}</td>
                <td colSpan={5}></td>
              </tr>
            ) : (
              <tr><td colSpan={11} className="tp-empty"></td></tr>
            )}

            {/* ===== FINAL FIGURE ===== */}
            <tr><td colSpan={11} style={{ height: '4px', border: 'none' }}></td></tr>
            <tr>
              <td colSpan={3} className="tp-center tp-bold" style={{ background: '#e8e8e8' }}>FINAL FIGURE</td>
              <td colSpan={2} className="tp-center tp-bold" style={{ background: '#e8e8e8' }}>AMOUNT</td>
              <td colSpan={6}></td>
            </tr>
            <tr>
              <td colSpan={3} className="tp-right tp-bold">TOTAL EXPENSE (H) (A+B+C+D+E+F+G)</td>
              <td colSpan={2} className="tp-right tp-bold">{formatNum(totalH)}</td>
              <td colSpan={6}></td>
            </tr>
            <tr>
              <td colSpan={3} className="tp-right tp-bold">TOTAL RECEIPTS (K) (DIESEL + NEFT)</td>
              <td colSpan={2} className="tp-right tp-bold">{formatNum(totalK)}</td>
              <td colSpan={4}></td>
              <td className="tp-total-label tp-right">TOTAL (J)</td>
              <td className="tp-bold tp-right">{formatNum(totalJ)}</td>
            </tr>
            <tr>
              <td colSpan={3} className="tp-right tp-bold">BALANCE (K-H)</td>
              <td colSpan={2} className="tp-right tp-bold">{formatNum(balance)}</td>
              <td colSpan={6}></td>
            </tr>
            {/* ===== REMARKS ===== */}
            <tr>
              <td className="tp-label">REMARKS</td>
              <td colSpan={10}>
                {balance > 0
                  ? `RS. ${formatNum(balance)} on hand cash or diesel`
                  : balance < 0
                    ? `RS. ${formatNum(Math.abs(balance))} due to driver`
                  : trip.remarks || ''}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
};

export default TripPrint;
